import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { ArrowRight, ArrowUpRight, CheckCircle } from "@phosphor-icons/react";
import { EASE_OUT, MaskWords, PageFrame, Photo, Seo, useMediaQuery } from "../../kit.jsx";
import { AAROH_IMAGES } from "../../data/images.js";
import { Rise, mailto } from "./shared.jsx";
import "./join.css";

const DOORS = [
  {
    title: "I'm a student",
    get: "Programmes to join, free project guides and real problems worth working on.",
    next: "Pick your starting point on the student page. Check upcoming events before you apply anywhere.",
    cta: "Student options",
    to: "/for-students",
    image: AAROH_IMAGES.studentPortrait,
    alt: "A smiling participant in the AAROH auditorium at IIIT Delhi",
    place: "AAROH, IIIT Delhi",
  },
  {
    title: "I'm from a school",
    get: "A conversation about an activity that fits your students and your school.",
    next: "Send your students' age range, group size, location and the activity you have in mind. We agree the scope with you before confirming anything.",
    cta: "School enquiries",
    to: "/for-schools",
    image: "/images/grid-photo-07-p7.jpg",
    alt: "Metamorphosis students in a group photo outside the Lecture Hall Complex at IIT Delhi",
    place: "Metamorphosis, IIT Delhi. GRID was outreach partner",
  },
  {
    title: "I want to mentor or partner",
    get: "A clear role, agreed before anything is announced, and credit that names what you did.",
    next: "Send a short brief: who you represent, what you can offer and when you are available.",
    cta: "Partnership enquiries",
    to: "/for-partners",
    image: AAROH_IMAGES.buildSession,
    alt: "A mentor working with students around laptops at AAROH",
    place: "AAROH, IIIT Delhi",
  },
  {
    title: "I want to join the team",
    get: "A named responsibility in one of GRID's working groups.",
    next: "Email the area you want, an example of your work and your weekly availability. An enquiry is not an offer of a role.",
    cta: "Join the team",
    to: "/student-opportunities/team",
    image: AAROH_IMAGES.studentSpeaker,
    alt: "A student speaking into a microphone at AAROH",
    place: "AAROH, IIIT Delhi",
  },
];

function Door({ door, index, open, onActivate }) {
  const reduce = useReducedMotion();
  return (
    <motion.li
      className={`jn-door ${open ? "is-open" : ""}`}
      initial={reduce ? false : { clipPath: "inset(100% 0% 0% 0% round 28px)" }}
      animate={{ clipPath: "inset(0% 0% 0% 0% round 28px)" }}
      transition={{ duration: 1.1, delay: 0.35 + index * 0.09, ease: EASE_OUT }}
    >
      <Link className="jn-door__link" to={door.to} onMouseEnter={onActivate} onFocus={onActivate}>
        <span className="jn-door__img">
          <Photo src={door.image} alt={door.alt} sizes="(max-width: 999px) 100vw, 40vw" priority={index < 2} />
        </span>
        <span className="jn-door__place">{door.place}</span>
        <span className="jn-door__plate">
          <strong className="jn-door__title">{door.title}</strong>
          <span className="jn-door__detail">
            <span className="jn-door__detail-inner">
              <span className="jn-door__pair">
                <span className="jn-mono">What you get</span>
                <span>{door.get}</span>
              </span>
              <span className="jn-door__pair">
                <span className="jn-mono">What happens next</span>
                <span>{door.next}</span>
              </span>
            </span>
          </span>
          <span className="jn-door__go">
            {door.cta}
            <ArrowUpRight weight="bold" aria-hidden="true" />
          </span>
        </span>
      </Link>
    </motion.li>
  );
}

function Doors() {
  const wide = useMediaQuery("(min-width: 1000px)");
  const [active, setActive] = useState(0);
  return (
    <section className="jn-wrap jn-gi-doors" aria-label="Choose how you want to join">
      <ul className="jn-doors">
        {DOORS.map((door, index) => (
          <Door key={door.to} door={door} index={index} open={!wide || active === index} onActivate={() => setActive(index)} />
        ))}
      </ul>
      <p className="jn-gi-intern">
        Looking for an internship? There is no confirmed listing right now.{" "}
        <Link className="gh-link" to="/student-opportunities/internships">
          What a listing must include <ArrowRight weight="bold" aria-hidden="true" />
        </Link>
      </p>
    </section>
  );
}

const ROLES = [
  "a student",
  "a school",
  "a mentor or partner",
  "an organisation",
  "someone who wants to join the team",
  "someone asking about internships",
];

function OneNote() {
  const reduce = useReducedMotion();
  const [values, setValues] = useState({ name: "", role: ROLES[0], email: "", extra: "" });
  const [ready, setReady] = useState(false);
  const set = (key) => (event) => {
    setValues({ ...values, [key]: event.target.value });
    setReady(false);
  };
  const body = [
    "Hi Project GRID,",
    "",
    `My name is ${values.name || "[your name]"}. I'm getting in touch as ${values.role}.`,
    ...(values.extra ? ["", values.extra] : []),
    "",
    `You can reach me at ${values.email || "[your email]"}.`,
  ].join("\n");

  return (
    <section className="jn-wrap jn-note" aria-labelledby="jn-note-title">
      <div className="jn-note__head">
        <MaskWords id="jn-note-title" className="jn-h2" text="Or say it in one note." />
        <p className="jn-lede">
          Fill the gaps and we write the email for you. Nothing is sent from this page. Your email app opens with the note ready, so you stay in control.
        </p>
      </div>
      <form
        className="jn-note__form"
        onSubmit={(event) => {
          event.preventDefault();
          setReady(true);
        }}
      >
        <p className="jn-note__sentence">
          Hi GRID, my name is{" "}
          <label className="jn-blank jn-blank--name">
            <span className="sr-only">Your name</span>
            <input value={values.name} onChange={set("name")} placeholder="your name" autoComplete="name" required />
          </label>
          . I'm getting in touch as{" "}
          <label className="jn-blank jn-blank--select">
            <span className="sr-only">You are</span>
            <select value={values.role} onChange={set("role")} style={{ width: `calc(${values.role.length}ch + 56px)` }}>
              {ROLES.map((role) => <option key={role}>{role}</option>)}
            </select>
          </label>
          . You can reach me at{" "}
          <label className="jn-blank jn-blank--email">
            <span className="sr-only">Your email address</span>
            <input type="email" value={values.email} onChange={set("email")} placeholder="you@example.com" autoComplete="email" required />
          </label>
          .
        </p>
        <label className="jn-field jn-note__extra">
          <span>Anything else we should know (optional)</span>
          <textarea rows={3} value={values.extra} onChange={set("extra")} placeholder="What you hope to make possible" />
        </label>
        <div className="jn-note__actions">
          <button className="gh-btn gh-btn--fill" type="submit">Prepare my email</button>
          <span className="jn-small">Opens your email app. It does not send anything by itself.</span>
        </div>
        <AnimatePresence>
          {ready && (
            <motion.div
              className="jn-ready"
              role="status"
              initial={reduce ? false : { opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={reduce ? undefined : { opacity: 0 }}
              transition={{ duration: 0.5, ease: EASE_OUT }}
            >
              <CheckCircle weight="fill" aria-hidden="true" />
              <span>Your note is ready. Send it from your email app.</span>
              <a className="gh-btn gh-btn--fill" href={mailto("Project GRID interest", body)}>
                Open email <ArrowUpRight weight="bold" aria-hidden="true" />
              </a>
            </motion.div>
          )}
        </AnimatePresence>
      </form>
    </section>
  );
}

export default function GetInvolvedPage() {
  return (
    <PageFrame className="rd-page jn-page jn-gi">
      <Seo
        title="Get Involved"
        path="/get-involved"
        description="Register interest in Project GRID programmes, mentorship, volunteering or partnerships."
      />
      <main>
        <header className="jn-wrap jn-gi-hero">
          <div className="jn-gi-hero__title">
            <Rise as="p" className="jn-mono" delay={0.05}>Join GRID</Rise>
            <MaskWords as="h1" className="jn-h1" text="Four ways into GRID." onMount delay={0.1} />
          </div>
          <Rise as="p" className="jn-lede" delay={0.4}>
            Tell us whether you are a student, mentor, volunteer, school or partner, and what you hope to make possible. Each door says what you get and what happens after you write.
          </Rise>
        </header>
        <Doors />
        <OneNote />
      </main>
    </PageFrame>
  );
}
